<template>
  <div class="path-preview">
    <header class="path-preview__head">
      <h3 class="path-preview__name">{{ path.name }}</h3>
      <p v-if="requirementText" class="path-preview__requirements">{{ requirementText }} Only</p>
    </header>

    <section v-for="rank in PathRanks" :key="rank" class="path-preview__rank">
      <div class="path-preview__rank-head">
        <div class="st-eyebrow">{{ rank }}</div>
        <p class="path-preview__hint">
          {{ rankOptions(rank).length > 1 ? 'Pick 1 ability at this rank.' : 'Gain this ability.' }}
        </p>
      </div>

      <div v-if="rankOptions(rank).length" class="path-preview__options">
        <article
          v-for="(option, index) in rankOptions(rank)"
          :key="index"
          class="path-preview__option"
        >
          <div v-if="option.abilities.length" class="path-preview__abilities">
            <AbilityCard
              v-for="ability in option.abilities"
              :key="ability.id || ability.name"
              :ability="ability"
            />
          </div>
          <p v-else class="path-preview__empty">No abilities authored.</p>
        </article>
      </div>
      <p v-else class="path-preview__empty">No options authored.</p>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { PathRanks, type IPath, type Option } from '@scrolldex/shared';
import AbilityCard from '../AbilityCard.vue';

const props = defineProps<{ path: IPath }>();

type RankKey = 'aspiring' | 'elite' | 'mighty' | 'legendary';

function rankOptions(rank: string): Option[] {
  return props.path[rank.toLowerCase() as RankKey].options;
}

const requirementText = computed(() => {
  const requirements: string[] = [];
  requirements.push(...props.path.requirements.nonKeywords.map((keyword) => `non-${keyword}`));
  requirements.push(...props.path.requirements.keywords);
  return requirements.join(' ');
});
</script>

<style scoped>
.path-preview {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-4);
}
.path-preview__head {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-1);
}
.path-preview__name {
  font-size: var(--st-text-lg);
  font-weight: var(--st-weight-bold);
}
.path-preview__requirements {
  margin: 0;
  color: var(--st-text-muted);
  font-size: var(--st-text-sm);
  font-style: italic;
}
.path-preview__rank,
.path-preview__option {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
}
.path-preview__rank-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--st-space-2);
}
.path-preview__hint,
.path-preview__empty {
  margin: 0;
  color: var(--st-text-muted);
  font-size: var(--st-text-sm);
}
.path-preview__options,
.path-preview__abilities {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
}
</style>
